import { isNetworkError } from "@/common/utils/is-network-error";
import { trpc } from "@/common/utils/trpc";
import { useCallback } from "react";
import { useCacheStorage } from "@/setup/frontend/storage/CacheStorageContext";
import {
	getCareFormDetail,
	getCareFormEdit,
	listCareFormEdits,
	removeCareFormEdit,
	getCareFormEditLastModified,
} from "../utils/storage";
import { useLogger } from "@/logging/client/LoggerContext";
import type { BrowserLogger } from "@/logging/client/BrowserLogger";

// Module-level state to track pending syncs for deduplication
const pendingSyncs = new Set<string>();

// Standalone function for syncing individual care form edit
export async function syncCareFormEdit(
	storage: any,
	utils: any,
	logger: BrowserLogger,
	id: number,
	offline_id?: string,
): Promise<{ success: boolean; error?: any }> {
	const syncKey = offline_id ?? id.toString();

	// Check if sync is already pending
	if (pendingSyncs.has(syncKey)) {
		return { success: false, error: "Sync already in progress" };
	}

	// Mark as pending
	pendingSyncs.add(syncKey);

	try {
		const storedEdit = await getCareFormEdit(storage, id, offline_id);
		const lastModified = await getCareFormEditLastModified(
			storage,
			id,
			offline_id,
		);

		if (!storedEdit) {
			return { success: false, error: "No stored edit found" };
		}

		try {
			await utils.client.careForms.saveInProgress.mutate(storedEdit, {
				context: {
					offline: true,
					offlineTimestamp: lastModified,
				},
			});

			// Remove from cache after successful sync
			const latestLastModified = await getCareFormEditLastModified(
				storage,
				id,
				offline_id,
			);
			if (latestLastModified === lastModified) {
				await removeCareFormEdit(storage, id, offline_id);
			}
			utils.careForms.detail.invalidate({ id });

			logger.info(`Synced care form edit ${id}`);

			return { success: true };
		} catch (error) {
			logger.error(`Failed to sync care form edit ${id}: ${error}`);
			if (!isNetworkError(error)) {
				// Remove from cache if it's not a network error and it's older than 2 days (e.g., validation error)
				if (
					lastModified &&
					new Date(lastModified) <
						new Date(Date.now() - 1000 * 60 * 60 * 24 * 2) // 2 days ago
				) {
					await removeCareFormEdit(storage, id, offline_id);
				}
			}
			// For network errors, keep the edit in the cache for retrying later
			return { success: false, error };
		}
	} finally {
		// Remove from pending syncs
		pendingSyncs.delete(syncKey);
	}
}

// Hook for syncing individual care form edit
export function useSyncCareFormEdit() {
	const utils = trpc.useUtils();
	const storage = useCacheStorage();
	const logger = useLogger();
	return useCallback(
		async (id: number, offline_id?: string) => {
			return await syncCareFormEdit(storage, utils, logger, id, offline_id);
		},
		[storage, utils],
	);
}

export function useSyncOfflineCareFormEdits() {
	const utils = trpc.useUtils();
	const storage = useCacheStorage();
	const logger = useLogger();

	return useCallback(async () => {
		const edits = await listCareFormEdits(storage);

		if (edits.length === 0) {
			return;
		}

		// Process each offline care form edit using the standalone function
		const results = await Promise.allSettled(
			edits.map((id) =>
				syncCareFormEdit(storage, utils, logger, id, undefined),
			),
		);

		// Return summary of results
		return {
			total: edits.length,
			successful: results.filter(
				(r) => r.status === "fulfilled" && r.value.success,
			).length,
			failed: results.filter(
				(r) =>
					r.status === "rejected" ||
					(r.status === "fulfilled" && !r.value.success),
			).length,
		};
	}, [storage, utils]);
}
